import { useEffect, useRef, useState } from 'react'
import { api, urlArchivo, enGo } from '../api'
import { Modal, Button, Spin, Typography, Alert, Space, message } from 'antd'
import { ClearOutlined, EditOutlined } from '@ant-design/icons'
import useIsMobile from '../hooks/useIsMobile'

const { Text } = Typography

// Recuadro para firmar con el dedo (o el ratón). Avisa con onCambio(true/false)
// de si ya hay una firma de verdad, no un toque suelto.
function PanelFirma({ lienzoRef, onCambio }) {
  const dibujando = useRef(false)
  const recorrido = useRef(0)
  const ultimo = useRef(null)

  useEffect(() => {
    const c = lienzoRef.current
    let anchoAnterior = 0
    const ajustar = () => {
      // offsetWidth y no getBoundingClientRect: mientras el Modal se abre está escalado
      const ancho = c.offsetWidth, alto = c.offsetHeight
      // En el móvil, al esconderse la barra del navegador salta "resize" sin cambiar
      // el ancho: no hay que borrar la firma por eso
      if (!ancho || ancho === anchoAnterior) return
      anchoAnterior = ancho
      const dpr = window.devicePixelRatio || 1
      c.width = ancho * dpr
      c.height = alto * dpr
      const ctx = c.getContext('2d')
      ctx.scale(dpr, dpr)
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#0b2a5b'
      recorrido.current = 0
      onCambio(false)
    }
    ajustar()
    window.addEventListener('resize', ajustar)
    return () => window.removeEventListener('resize', ajustar)
  }, [lienzoRef, onCambio])

  const punto = (e) => {
    const r = lienzoRef.current.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }
  const empezar = (e) => {
    e.preventDefault()
    lienzoRef.current.setPointerCapture(e.pointerId)
    dibujando.current = true
    ultimo.current = punto(e)
  }
  const mover = (e) => {
    if (!dibujando.current) return
    const p = punto(e)
    const ctx = lienzoRef.current.getContext('2d')
    ctx.beginPath()
    ctx.moveTo(ultimo.current.x, ultimo.current.y)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    recorrido.current += Math.hypot(p.x - ultimo.current.x, p.y - ultimo.current.y)
    ultimo.current = p
    if (recorrido.current > 60) onCambio(true)
  }
  const terminar = () => { dibujando.current = false }

  return (
    <canvas
      ref={lienzoRef}
      onPointerDown={empezar}
      onPointerMove={mover}
      onPointerUp={terminar}
      onPointerCancel={terminar}
      style={{ width: '100%', height: '150px', border: '1px dashed #8c8c8c', borderRadius: '8px', background: '#fff', touchAction: 'none', cursor: 'crosshair', display: 'block' }}
    />
  )
}

// El trabajador ve el documento (como imágenes: no se descarga) y firma que lo ha recibido.
export default function FirmaDocumento({ documento, onCerrar, onFirmado }) {
  const isMobile = useIsMobile()
  const [paginas, setPaginas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [hayFirma, setHayFirma] = useState(false)
  const [firmando, setFirmando] = useState(false)
  const lienzoRef = useRef(null)

  useEffect(() => {
    let urls = []
    let cancelado = false
    ;(async () => {
      try {
        const info = await api(`/documentos-trabajador/${documento.id}/vista`)
        for (let n = 1; n <= info.paginas; n++) {
          const u = await urlArchivo(`/documentos-trabajador/${documento.id}/vista/${n}`)
          if (cancelado) { URL.revokeObjectURL(u); return }
          urls.push(u)
          setPaginas([...urls])
        }
      } catch (err) {
        if (!cancelado) setError(err.message)
      }
      if (!cancelado) setCargando(false)
    })()
    return () => { cancelado = true; urls.forEach(u => URL.revokeObjectURL(u)) }
  }, [documento.id])

  const borrar = () => {
    const c = lienzoRef.current
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    setHayFirma(false)
  }

  const firmar = async () => {
    setFirmando(true)
    try {
      await api(`/documentos-trabajador/${documento.id}/firmar`, {
        method: 'POST',
        body: JSON.stringify({ firma: lienzoRef.current.toDataURL('image/png') }),
      })
      message.success('Documento firmado. Ya puedes descargarlo.')
      onFirmado()
    } catch (err) {
      message.error(err.message)
    }
    setFirmando(false)
  }

  const puedeFirmar = documento.requiere_firma && !documento.firmado

  return (
    <Modal
      open
      title={documento.nombre}
      onCancel={onCerrar}
      footer={null}
      width={isMobile ? '100%' : 760}
      style={isMobile ? { top: 0, padding: 0, maxWidth: '100%' } : { top: 20 }}
      styles={{ body: { padding: isMobile ? '4px 0' : undefined } }}
      destroyOnHidden
    >
      <div
        onContextMenu={e => e.preventDefault()}
        style={{ maxHeight: puedeFirmar ? (isMobile ? '45vh' : '55vh') : '75vh', overflowY: 'auto', background: '#e8e8e8', borderRadius: '8px', padding: '8px' }}
      >
        {error && <Alert type="error" showIcon title={error} />}
        {paginas.map((u, i) => (
          <img key={u} src={u} alt={`Página ${i + 1}`} draggable={false}
            style={{ width: '100%', display: 'block', marginBottom: '8px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.15)', userSelect: 'none' }} />
        ))}
        {cargando && <div style={{ textAlign: 'center', padding: '30px' }}><Spin description="Abriendo el documento..." /></div>}
      </div>

      {puedeFirmar && !error && enGo() && (
        <Alert type="warning" showIcon style={{ marginTop: '12px' }}
          title="Estás en GO!: aquí el trabajador vería el recuadro para firmar"
          description="La firma la tiene que hacer él desde su usuario." />
      )}

      {puedeFirmar && !error && !enGo() && (
        <div style={{ marginTop: '12px' }}>
          <Text strong style={{ display: 'block', marginBottom: '2px' }}><EditOutlined /> He recibido este documento.</Text>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
            Firma con el dedo en el recuadro. Firmar solo indica que lo has recibido, no que estés de acuerdo con su contenido. Cuando firmes podrás descargarlo.
          </Text>
          <PanelFirma lienzoRef={lienzoRef} onCambio={setHayFirma} />
          <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: '10px' }}>
            <Button icon={<ClearOutlined />} onClick={borrar} disabled={firmando}>Borrar</Button>
            <Button type="primary" onClick={firmar} loading={firmando} disabled={!hayFirma || cargando} style={{ background: hayFirma && !cargando ? '#2E7D32' : undefined }}>
              Firmar
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  )
}
